// Collection page: native sort_by on change, and "Show 12 more" that appends the next page's cards
// (Section Rendering API ?section_id=). Without JS the sort form submits and "Show 12 more" is a next-page link.
// The first AUTO_BATCHES batches load by themselves as the shopper nears the bottom; after that the button
// waits for a click so the footer stays reachable. The loaded batches and scroll position are remembered per
// URL in sessionStorage so Back from a product lands where the shopper left off.
const controllers = new WeakMap();
const SEL = '[data-collection]';
const AUTO_BATCHES = 3;
const PREFETCH = '300px';

function memKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  return 'coll:' + url.pathname + url.search;
}
function readMem() {
  try { return JSON.parse(sessionStorage.getItem(memKey()) || 'null') || { batches: 0, y: 0 }; } catch (e) { return { batches: 0, y: 0 }; }
}
function writeMem(m) {
  try { sessionStorage.setItem(memKey(), JSON.stringify(m)); } catch (e) { /* private mode */ }
}

export function init(root) {
  if (root.dataset.collInit) return;
  root.dataset.collInit = '1';
  const ac = new AbortController();
  const { signal } = ac;
  controllers.set(root, ac);
  let batches = 0;          // batches appended on this page view (manual or auto)
  let autoKey = memKey();   // filter/sort state the auto count belongs to
  let autoCount = 0;        // auto-loads spent for autoKey
  let wantFocus = false;    // a click arrived while an auto-load was in flight: focus its first card

  root.addEventListener('change', (e) => {
    const sort = e.target.closest('[data-sort]');
    if (sort) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    }
  }, { signal });

  async function loadMore(more, { focus }) {
    if (more.getAttribute('aria-busy') === 'true') { if (focus) wantFocus = true; return false; }
    more.setAttribute('aria-busy', 'true');
    const url = new URL(more.href, window.location.href);
    url.searchParams.set('section_id', root.dataset.sectionId);
    try {
      const res = await fetch(url.toString(), { signal });
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const cards = [...doc.querySelectorAll('[data-coll-grid] > .card')];
      const grid = root.querySelector('[data-coll-grid]');
      const first = cards[0];
      grid.append(...cards);
      const shown = root.querySelector('[data-shown]');
      if (shown) shown.textContent = String(grid.querySelectorAll(':scope > .card').length);
      const next = doc.querySelector('[data-coll-more]');
      if (next) {
        more.href = next.getAttribute('href');
        more.removeAttribute('aria-busy');
      } else {
        more.remove();
      }
      batches += 1;
      writeMem({ batches, y: window.scrollY });
      const link = (focus || wantFocus) && first && first.querySelector('.card__name a');
      wantFocus = false;
      if (link) link.focus();
      return true;
    } catch (err) {
      if (signal.aborted) return false;
      window.location.assign(more.href); // fall back to the plain next-page link
      return false;
    }
  }

  root.addEventListener('click', (e) => {
    const more = e.target.closest('[data-coll-more]');
    if (!more || !root.contains(more)) return;
    e.preventDefault();
    loadMore(more, { focus: true });
  }, { signal });

  // Auto-load: watch the .more band; when it nears the viewport, spend one auto batch.
  const band = root.querySelector('.more');
  let io = null;
  if (band && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(async (entries) => {
      if (!entries.some((en) => en.isIntersecting)) return;
      const key = memKey();
      if (key !== autoKey) { autoKey = key; autoCount = 0; } // filters or sort changed: fresh allowance
      if (autoCount >= AUTO_BATCHES) return;
      const more = root.querySelector('[data-coll-more]');
      if (!more) return;
      autoCount += 1;
      const ok = await loadMore(more, { focus: false });
      if (ok && io) { io.unobserve(band); io.observe(band); } // re-check: the band may still be in view
    }, { rootMargin: PREFETCH });
    signal.addEventListener('abort', () => io.disconnect());
  }

  // Remember where the shopper was when they leave for a product page.
  window.addEventListener('pagehide', () => { if (batches) writeMem({ batches, y: window.scrollY }); }, { signal });

  // Back from a product: rebuild the batches that were open, then return to the same spot.
  (async () => {
    const mem = readMem();
    const want = Math.min(mem.batches || 0, 20);
    if (want > 0) {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      for (let i = 0; i < want; i += 1) {
        const more = root.querySelector('[data-coll-more]');
        if (!more) break;
        const ok = await loadMore(more, { focus: false });
        if (!ok) break;
      }
      autoCount = Math.min(batches, AUTO_BATCHES);
      if (mem.y) window.scrollTo(0, mem.y);
    }
    if (io && band) io.observe(band);
  })();
}

document.querySelectorAll(SEL).forEach(init);
document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll(SEL).forEach(init));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll(SEL).forEach((r) => { const c = controllers.get(r); if (c) c.abort(); });
});
